// src/pages/admin/AdminDashboard.jsx
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

/* ===================== styled ===================== */
const Page = styled.div`
  --gap: 16px;
  --radius: 12px;

  max-width: 1200px;
  margin: 0 auto;
  padding: calc(var(--header-height, 72px) + 24px) 16px 24px;
  padding-top: 10%;
  padding-bottom: 8rem;
  @media (max-width: 768px) {
    padding-top: 30%;
  }
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gap);
`;

const Title = styled.h2`
  margin: 0;
  letter-spacing: -0.02em;
`;

const Filters = styled.div`
  display: grid;
  grid-template-columns: 1fr 160px 160px 160px auto auto; /* 검색, 담당자, 타입, 상태, 검색버튼, 초기화 */
  gap: 8px;
  margin-bottom: 12px;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr 1fr;
    & > *:nth-child(n + 3) {
      grid-column: span 1;
    }
  }
`;

const Input = styled.input`
  height: 40px;
  padding: 0 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  outline: none;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, .15);
  }
`;

const Select = styled.select`
  height: 40px;
  padding: 0 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  background: #fff;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, .15);
    outline: none;
  }
`;

const ButtonBase = styled.button`
  height: 40px;
  width: 90px;
  padding: 0 14px;
  border-radius: 9999px;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform .05s ease, box-shadow .15s ease, background .15s ease, color .15s ease;

  &:active { transform: translateY(1px); }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const PrimaryButton = styled(ButtonBase)`
  background: #608cecff;
  color: #fff;
  &:hover { background: #1e4fd4; }
`;

const SecondaryButton = styled(ButtonBase)`
  color: #8e8d8dff;
  &:hover { color: #131313; }
`;

const GhostButton = styled(ButtonBase)`
  background: #fff;
  color: #374151;
  border: 1px solid #e5e7eb;
  &:hover { background: #f9fafb; }
`;

const ErrorAlert = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  padding: 10px 12px;
  border-radius: 10px;
  margin: 8px 0 12px;
  white-space: pre-wrap;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.15fr .85fr;
  gap: var(--gap);

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: var(--radius);
  background: #fff;
  overflow: hidden;
`;

const TableWrap = styled.div`
  max-height: 60vh;
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead tr {
    background: #f8fafc;
    position: sticky;
    top: 0;
    z-index: 1;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-weight: 700;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const Tr = styled.tr`
  border-top: 1px solid #f3f4f6;
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? '#eef6ff' : 'transparent')};

  &:hover { background: ${({ $selected }) => ($selected ? '#e6f0ff' : '#fafafa')}; }
`;

const TrEmpty = styled.tr`
  td {
    padding: 16px;
    color: #6b7280;
    text-align: center;
  }
`;

const Td = styled.td`
  padding: 10px 12px;
  vertical-align: middle;
  ${({ mono }) => mono && `font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace; font-size:12px;`}
  max-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 9999px;
  font-weight: 700;
  font-size: 12px;
  color: ${({ status }) =>
      status === 'RECEIVED' ? '#1d4ed8' :
          status === 'IN_PROGRESS' ? '#b45309' :
              status === 'CLOSED' ? '#166534' : '#374151'};
  background: ${({ status }) =>
      status === 'RECEIVED' ? '#dbeafe' :
          status === 'IN_PROGRESS' ? '#fef3c7' :
              status === 'CLOSED' ? '#dcfce7' : '#e5e7eb'};
`;

const PanelTitle = styled.h3`
  margin: 16px 16px 8px;
`;

const Placeholder = styled.div`
  margin: 0 16px 16px;
  color: #6b7280;
`;

const Form = styled.div`
  display: grid;
  gap: 12px;
  padding: 0 16px 16px;
`;

const Field = styled.div`
  display: grid;
  gap: 6px;
  ${({ flex }) => flex && `flex:1;`}
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.div`
  font-weight: 700;
  color: #374151;
`;

const ReadOnlyBox = styled.div`
  min-height: 40px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
`;

const Code = styled.code`
  display: inline-block;
  min-height: 40px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 12px;
  word-break: break-all;
`;

const Textarea = styled.textarea`
  min-height: 120px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  resize: vertical;

  &:read-only {
    background: #f9fafb;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, .15);
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #f1f5f9;
  margin: 4px 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

// TopBar 오른쪽 버튼 묶음 컨테이너
const Right = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

/* ===================== utils ===================== */
function mapStatusLabel(s) {
  if (s === 'RECEIVED') return '접수';
  if (s === 'IN_PROGRESS') return '진행중';
  if (s === 'CLOSED') return '종결';
  return s || '-';
}
function mapTypeLabel(t) {
  if (t === 'ANONYMOUS') return '익명';
  if (t === 'REALNAME') return '실명';
  return t || '-';
}
function humanSize(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}
// 날짜만(년-월-일) 표시
function formatYMD(s) {
  if (!s) return '-';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '-';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function AdminDashboard() {
  const API = process.env.REACT_APP_API_BASE || 'http://localhost:8080';
  const nav = useNavigate();

  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);

  const [q, setQ] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState(''); // 담당자 필터
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // 상태 변경 감지용(서버 기록을 신뢰하되, 필요시 비교에 사용)
  const origStatusRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const qs = new URLSearchParams();
      if (q) qs.set('q', q);
      if (assigneeFilter) qs.set('assignee', assigneeFilter);
      if (type) qs.set('type', type);
      if (status) qs.set('status', status);

      const r = await fetch(`${API}/api/reports?${qs.toString()}`, {
        credentials: 'include',
      });

      if (r.status === 401 || r.status === 403) {
        nav('/admin/login', { replace: true });
        return;
      }
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || '목록 조회 실패');
      }
      const data = await r.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e.message || '에러가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const open = async (ref) => {
    setErr('');
    try {
      const r = await fetch(`${API}/api/reports/${ref}`, {
        credentials: 'include',
      });
      if (r.status === 401 || r.status === 403) {
        nav('/admin/login', { replace: true });
        return;
      }
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || '상세 조회 실패');
      }
      const data = await r.json();
      setSel(data);
      origStatusRef.current = data?.status ?? null;
    } catch (e) {
      setErr(e.message || '상세 조회 실패');
    }
  };

  const save = async () => {
    if (!sel) return;
    setSaving(true);
    setErr('');
    try {
      // 날짜 표시는 화면에서만 처리 → 서버에는 statusChangedAt을 보내지 않음(서버가 기록)
      const body = {
        status: sel.status,
        actionNotes: sel.actionNotes || '',
        assignee: sel.assignee || '',
      };

      const r = await fetch(`${API}/api/reports/${sel.referenceId}/action`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (r.status === 401 || r.status === 403) {
        nav('/admin/login', { replace: true });
        return;
      }
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || '저장 실패');
      }
      alert('저장되었습니다.');
      await load();
      await open(sel.referenceId);
    } catch (e) {
      setErr(e.message || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (_) {}
    nav('/admin/login', { replace: true });
  };

  const onKeyDownSearch = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      load();
    }
  };

  const clearFilters = () => {
    setQ('');
    setAssigneeFilter('');
    setType('');
    setStatus('');
    setSel(null);
    setRows([]);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const assigneeOptions = Array.from(
      new Set(rows.map(r => (r.assignee || '').trim()).filter(Boolean))
  );

  return (
      <Page>
        <TopBar>
          <Title>관리자 대시보드</Title>
          <Right>
            <GhostButton onClick={() => nav('/admin/inquiries')}>문의 관리</GhostButton>
            <GhostButton onClick={() => nav('/admin/news')}>뉴스 관리</GhostButton>
            <SecondaryButton onClick={logout}>로그아웃</SecondaryButton>
          </Right>
        </TopBar>

        <Filters>
          <Input
              placeholder="검색 (제목/내용/대상/참조번호)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKeyDownSearch}
          />

          <Input
              placeholder="담당자"
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              onKeyDown={onKeyDownSearch}
              list="assignee-list"
          />
          <datalist id="assignee-list">
            {assigneeOptions.map((opt) => (
                <option key={opt} value={opt} />
            ))}
          </datalist>

          <Select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">타입(전체)</option>
            <option value="ANONYMOUS">익명</option>
            <option value="REALNAME">실명</option>
          </Select>
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">상태(전체)</option>
            <option value="RECEIVED">접수</option>
            <option value="IN_PROGRESS">진행중</option>
            <option value="CLOSED">종결</option>
          </Select>
          <PrimaryButton onClick={load} disabled={loading}>
            {loading ? '불러오는 중…' : '검색'}
          </PrimaryButton>
          <GhostButton onClick={clearFilters}>초기화</GhostButton>
        </Filters>

        {err && <ErrorAlert>{err}</ErrorAlert>}

        <Grid>
          {/* 목록 */}
          <Card>
            <TableWrap>
              <Table>
                <thead>
                <tr>
                  <Th style={{ width: 280 }}>참조번호</Th>
                  <Th>제목</Th>
                  <Th style={{ width: 140 }}>담당자</Th>
                  <Th style={{ width: 120 }}>상태</Th>
                  <Th style={{ width: 140 }}>상태변경일</Th> {/* 날짜만 */}
                  <Th style={{ width: 140 }}>생성일</Th>     {/* 날짜만 */}
                </tr>
                </thead>
                <tbody>
                {rows.map((r) => {
                  const selected = sel && sel.referenceId === r.referenceId;
                  const created = formatYMD(r.createdAt);
                  const changed = formatYMD(r.statusChangedAt);
                  return (
                      <Tr
                          key={r.referenceId}
                          $selected={selected}
                          onClick={() => open(r.referenceId)}
                      >
                        <Td mono>{r.referenceId}</Td>
                        <Td title={r.title || ''}>{r.title || '(제목 없음)'}</Td>
                        <Td title={r.assignee || ''}>{r.assignee || '-'}</Td>
                        <Td>
                          <StatusBadge status={r.status}>{mapStatusLabel(r.status)}</StatusBadge>
                        </Td>
                        <Td>{changed}</Td>
                        <Td>{created}</Td>
                      </Tr>
                  );
                })}

                {rows.length === 0 && !loading && (
                    <TrEmpty>
                      <td colSpan={6}>데이터 없음</td>
                    </TrEmpty>
                )}
                {loading && (
                    <TrEmpty>
                      <td colSpan={6}>로딩 중…</td>
                    </TrEmpty>
                )}
                </tbody>
              </Table>
            </TableWrap>
          </Card>

          {/* 상세/조치 */}
          <Card>
            <PanelTitle>상세 / 조치</PanelTitle>
            {!sel ? (
                <Placeholder>왼쪽 목록에서 항목을 선택하세요.</Placeholder>
            ) : (
                <Form>
                  <Field>
                    <Label>제목</Label>
                    <ReadOnlyBox>{sel.title || '-'}</ReadOnlyBox>
                  </Field>

                  <FieldRow>
                    <Field flex>
                      <Label>참조번호</Label>
                      <Code>{sel.referenceId}</Code>
                    </Field>
                    <Field>
                      <Label>타입</Label>
                      <ReadOnlyBox>{mapTypeLabel(sel.type) || '-'}</ReadOnlyBox>
                    </Field>
                  </FieldRow>

                  <FieldRow>
                    <Field flex>
                      <Label>대상 부서/업무</Label>
                      <ReadOnlyBox>{sel.targetDept || '-'}</ReadOnlyBox>
                    </Field>
                    <Field>
                      <Label>대상자</Label>
                      <ReadOnlyBox>{sel.targetName || '-'}</ReadOnlyBox>
                    </Field>
                  </FieldRow>

                  <Field>
                    <Label>내용</Label>
                    <Textarea value={sel.content || ''} readOnly />
                  </Field>

                  <Divider />

                  <FieldRow>
                    <Field>
                      <Label>상태</Label>
                      <Select
                          value={sel.status}
                          onChange={(e) => setSel({ ...sel, status: e.target.value })}
                      >
                        <option value="RECEIVED">접수</option>
                        <option value="IN_PROGRESS">진행중</option>
                        <option value="CLOSED">종결</option>
                      </Select>
                    </Field>
                    <Field flex>
                      <Label>첨부</Label>
                      <ReadOnlyBox>
                        {sel.attachmentName
                            ? `${sel.attachmentName}${sel.attachmentSize ? ` (${humanSize(sel.attachmentSize)})` : ''}`
                            : '없음'}
                      </ReadOnlyBox>
                    </Field>
                  </FieldRow>

                  {/* 담당자 + 날짜들 */}
                  <FieldRow>
                    <Field>
                      <Label>담당자</Label>
                      <Input
                          value={sel.assignee ?? ''}
                          onChange={(e) => setSel({ ...sel, assignee: e.target.value })}
                          placeholder="담당자 이름 또는 계정"
                          list="assignee-list"
                      />
                    </Field>
                    <Field>
                      <Label>상태변경일</Label>
                      <ReadOnlyBox>{formatYMD(sel.statusChangedAt)}</ReadOnlyBox>
                    </Field>
                  </FieldRow>

                  <FieldRow>
                    <Field>
                      <Label>생성일</Label>
                      <ReadOnlyBox>{formatYMD(sel.createdAt)}</ReadOnlyBox>
                    </Field>
                    <Field>{/* 자리 맞춤용 비움 */}</Field>
                  </FieldRow>

                  <Field>
                    <Label>조치/답변</Label>
                    <Textarea
                        value={sel.actionNotes || ''}
                        onChange={(e) => setSel({ ...sel, actionNotes: e.target.value })}
                        placeholder="조치 내용 또는 답변을 입력하세요."
                    />
                  </Field>

                  <Actions>
                    <PrimaryButton onClick={save} disabled={saving}>
                      {saving ? '저장 중...' : '저장'}
                    </PrimaryButton>
                  </Actions>
                </Form>
            )}
          </Card>
        </Grid>
      </Page>
  );
}
